import React from "react";
import { CheckCircle2, DatabaseBackup, X, XCircle } from "lucide-react";
import { backupPercent, dismissBackupDownload, fmtMb, useBackupDownload } from "../../lib/backupDownload";

/** Floating progress card for the backup download — visible on every page while it runs (or just finished/failed). */
export const BackupDownloadIndicator: React.FC = () => {
  const s = useBackupDownload();
  if (s.status === "idle") return null;

  const downloading = s.status === "downloading";
  const pct = backupPercent(s);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 z-50 rounded-xl border border-slate-200 bg-white p-3.5 shadow-xl sm:left-auto sm:right-24 sm:w-80"
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            s.status === "done" ? "bg-emerald-50 text-emerald-600" : s.status === "error" ? "bg-red-50 text-red-600" : "bg-brand-50 text-brand-600"
          }`}
        >
          {s.status === "done" ? <CheckCircle2 className="h-5 w-5" /> : s.status === "error" ? <XCircle className="h-5 w-5" /> : <DatabaseBackup className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">
            {s.status === "done" ? (s.nothingNew ? "No new files" : "Backup downloaded") : s.status === "error" ? "Backup failed" : "Downloading backup"}
          </p>
          {s.status === "preparing" && <p className="text-xs text-slate-500">Building the backup on the server…</p>}
          {downloading && (
            <p className="text-xs text-slate-500">
              {fmtMb(s.received)} / {fmtMb(Math.max(s.total, s.received))} · {pct}%
            </p>
          )}
          {s.status === "done" && <p className="text-xs text-slate-500">{s.nothingNew ? "Nothing has been added since the last backup with files." : `${fmtMb(s.bytes)} saved to your Downloads folder.`}</p>}
          {s.status === "error" && <p className="break-words text-xs text-red-600">{s.message}</p>}
          {(s.status === "preparing" || downloading) && (
            <>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full bg-brand-600 transition-[width] duration-300 ${downloading ? "" : "w-1/4 animate-pulse"}`}
                  style={downloading ? { width: `${pct}%` } : undefined}
                />
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">You can keep working — just don't close or reload this tab.</p>
            </>
          )}
        </div>
        {(s.status === "done" || s.status === "error") && (
          <button type="button" onClick={dismissBackupDownload} aria-label="Dismiss" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
